import React from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useGoogleMaps } from "./useGoogleMaps";
import { TOKENS } from "./styled";

export type ResolvedAddress = {
  address: string; // street line, e.g. "12 rue de Paris"
  city: string;
  postal_code: string;
  insee_code?: string;
  lat?: number;
  lng?: number;
  formatted_address: string;
};

type Prediction = {
  place_id: string;
  description: string;
  main_text: string;
  secondary_text: string;
};

type Props = {
  apiKey: string | null;
  value: ResolvedAddress | null;
  onChange: (next: ResolvedAddress | null) => void;
  label?: string;
};

const AddressAutocomplete: React.FC<Props> = ({
  apiKey,
  value,
  onChange,
  label = "Adresse",
}) => {
  const isLoaded = useGoogleMaps(apiKey);
  const [input, setInput] = React.useState(value?.formatted_address ?? "");
  const [options, setOptions] = React.useState<Prediction[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Each suggestion stores the original placePrediction so we can call
  // toPlace().fetchFields() on selection without an extra service instance.
  const suggestionByIdRef = React.useRef<Record<string, any>>({});
  const sessionTokenRef = React.useRef<any>(null);

  React.useEffect(() => {
    if (!isLoaded) return;
    const places = window.google.maps.places;
    sessionTokenRef.current = new places.AutocompleteSessionToken();
  }, [isLoaded]);

  React.useEffect(() => {
    if (!isLoaded) return;
    if (input.length < 3) {
      setOptions([]);
      return;
    }
    if (value && input === value.formatted_address) {
      // Don't re-search after a selection.
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      const places = window.google.maps.places;
      if (!places?.AutocompleteSuggestion) {
        setLoading(false);
        return;
      }
      try {
        const { suggestions } =
          await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input,
            sessionToken: sessionTokenRef.current,
            includedRegionCodes: ["fr"],
            language: "fr",
          });
        const next: Prediction[] = [];
        const map: Record<string, any> = {};
        for (const s of suggestions ?? []) {
          const p = s.placePrediction;
          if (!p) continue;
          next.push({
            place_id: p.placeId,
            description: p.text?.text ?? "",
            main_text: p.mainText?.text ?? p.text?.text ?? "",
            secondary_text: p.secondaryText?.text ?? "",
          });
          map[p.placeId] = p;
        }
        suggestionByIdRef.current = map;
        setOptions(next);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[ServiceEnDirect] Places fetchAutocompleteSuggestions:", e);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [input, isLoaded, value]);

  const handleSelect = async (prediction: Prediction) => {
    const placePrediction = suggestionByIdRef.current[prediction.place_id];
    if (!placePrediction) return;
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: ["formattedAddress", "location", "addressComponents"],
      });
      const components: any[] = place.addressComponents ?? [];
      const get = (...types: string[]) =>
        components.find((c) => types.every((t) => c.types.includes(t)));

      const streetNumber = get("street_number")?.longText ?? "";
      const route = get("route")?.longText ?? "";
      const city =
        get("locality")?.longText ??
        get("postal_town")?.longText ??
        get("administrative_area_level_2")?.longText ??
        "";
      const postalCode = get("postal_code")?.longText ?? "";
      const street = [streetNumber, route].filter(Boolean).join(" ");
      // place.location can be a LatLng instance (`.lat()` method) or a
      // plain LatLngLiteral (`.lat` number property) depending on the
      // build, so support both.
      const loc: any = place.location;
      const lat: number | undefined =
        typeof loc?.lat === "function" ? loc.lat() : loc?.lat;
      const lng: number | undefined =
        typeof loc?.lng === "function" ? loc.lng() : loc?.lng;

      const resolved: ResolvedAddress = {
        address: street || prediction.main_text,
        city,
        postal_code: postalCode,
        lat,
        lng,
        formatted_address: place.formattedAddress ?? prediction.description,
      };
      onChange(resolved);
      setInput(resolved.formatted_address);
      // Refresh session token for the next search session.
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ServiceEnDirect] Places fetchFields:", e);
    }
  };

  return (
    <Autocomplete<Prediction, false, false, true>
      fullWidth
      freeSolo
      filterOptions={(x) => x}
      options={options}
      value={null}
      inputValue={input}
      onInputChange={(_, v, reason) => {
        if (reason === "input") {
          setInput(v);
          if (value) onChange(null);
        } else if (reason === "clear") {
          setInput("");
          onChange(null);
        }
      }}
      onChange={(_, picked) => {
        if (picked && typeof picked !== "string") {
          handleSelect(picked as Prediction);
        }
      }}
      getOptionLabel={(o) => (typeof o === "string" ? o : o.description)}
      isOptionEqualToValue={(a: any, b: any) => a?.place_id === b?.place_id}
      loading={loading}
      noOptionsText={
        !isLoaded
          ? "Chargement de Google Maps…"
          : input.length < 3
          ? "Tapez au moins 3 caractères"
          : "Aucun résultat"
      }
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.place_id}
          sx={{
            padding: "12px 16px !important",
            borderBottom: `1px solid ${TOKENS.GRAY_100}`,
            transition: "background 0.15s",
            "&:hover, &.Mui-focused": {
              background: `${TOKENS.GRAY_100} !important`,
            },
            "&:last-child": { borderBottom: "none" },
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "rgba(49,130,206,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            <LocationOnIcon
              sx={{ color: TOKENS.ACCENT_BLUE, fontSize: 18 }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                color: TOKENS.PRIMARY_BLUE,
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              {option.main_text}
            </Typography>
            {option.secondary_text && (
              <Typography
                sx={{
                  color: TOKENS.GRAY_600,
                  fontSize: "0.8rem",
                }}
              >
                {option.secondary_text}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      slotProps={{
        paper: {
          sx: {
            borderRadius: "10px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.12)",
            mt: 0.5,
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              background: TOKENS.GRAY_300,
              borderRadius: 3,
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Ex : 12 rue des Lilas, 31000 Toulouse"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              background: TOKENS.GRAY_100,
              transition: "all 0.2s",
              "& fieldset": { borderColor: "transparent" },
              "&:hover": {
                background: TOKENS.WHITE,
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              },
              "&.Mui-focused": {
                background: TOKENS.WHITE,
                boxShadow: "0 4px 12px rgba(49,130,206,0.15)",
                "& fieldset": {
                  borderColor: TOKENS.ACCENT_BLUE,
                  borderWidth: 2,
                },
              },
            },
            "& .MuiInputLabel-root": {
              color: TOKENS.GRAY_500,
              "&.Mui-focused": { color: TOKENS.ACCENT_BLUE },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: TOKENS.ACCENT_BLUE }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default AddressAutocomplete;
